import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import {
    MagnifyingGlass,
    PhoneCall,
    Briefcase,
    UserCircle,
    Question,
    Globe,
    MapPin,
    Buildings,
    Tag,
    Clock,
    WarningCircle,
    X,
} from "@phosphor-icons/react";
import { fetchPhoneInfo, getAccessToken } from "@/lib/api";
import { fmtDate } from "@/lib/format";

const VERDICT_STYLES = {
    agent: {
        box: "bg-orange-50 border-orange-200",
        title: "text-orange-900",
        text: "text-orange-700",
        phone: "text-orange-900",
        icon: Briefcase,
    },
    suspected_agent: {
        box: "bg-amber-50 border-amber-200",
        title: "text-amber-900",
        text: "text-amber-700",
        phone: "text-amber-900",
        icon: Briefcase,
    },
    owner: {
        box: "bg-emerald-50 border-emerald-200",
        title: "text-emerald-900",
        text: "text-emerald-700",
        phone: "text-emerald-900",
        icon: UserCircle,
    },
    unknown: {
        box: "bg-secondary/60 border-border/60",
        title: "text-foreground",
        text: "text-muted-foreground",
        phone: "text-foreground",
        icon: Question,
    },
};

const borderClass = "border border-black/10 box-border";

const VerdictCard = ({ info }) => {
    const s = VERDICT_STYLES[info.verdict] || VERDICT_STYLES.unknown;
    const Icon = s.icon;

    return (
        <section
            className={`min-w-0 rounded-xl border p-3 sm:p-4 lg:p-5 ${s.box}`}
            data-testid="phone-info-verdict"
        >
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white shadow-sm sm:h-12 sm:w-12">
                        <Icon size={24} weight="duotone" className={s.title} />
                    </div>
                    <div className="min-w-0">
                        <div className={`text-base font-bold leading-tight sm:text-xl lg:text-2xl ${s.title}`}>
                            {info.verdictTitle}
                        </div>
                    </div>
                </div>

                <div className="min-w-0 text-right">
                    <div className={`price-tabular text-lg font-bold leading-tight sm:text-2xl lg:text-3xl ${s.phone}`}>
                        {info.phoneFormatted}
                    </div>
                    {info.names?.length > 0 && (
                        <div className={`mt-1 max-w-full truncate text-xs sm:max-w-md sm:text-sm ${s.text}`}>
                            {info.names.join(" · ")}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const StatCard = ({ label, value, sub, icon, small }) => (
    <div className="min-w-0 rounded-xl border border-black/10 bg-card p-2.5 sm:p-3.5 lg:p-4">
        <div className="flex min-w-0 items-center gap-1.5 text-xs leading-tight text-muted-foreground sm:text-sm">
            {icon}
            <span className="truncate">{label}</span>
        </div>
        <div
            className={`price-tabular mt-1 truncate font-bold leading-tight ${small ? "text-sm sm:text-lg lg:text-xl" : "text-xl sm:text-2xl lg:text-3xl"
                }`}
        >
            {value}
        </div>
        {sub && (
            <div className="mt-1 truncate text-[11px] leading-tight text-muted-foreground sm:text-xs">
                {sub}
            </div>
        )}
    </div>
);

const BarList = ({ title, icon, items }) => {
    const max = Math.max(1, ...(items || []).map((item) => item.count));

    return (
        <section className="min-w-0 min-h-24 rounded-xl border border-black/10 bg-card p-3 sm:p-4">
            <div className="mb-2 flex min-w-0 items-center gap-1.5 text-sm font-semibold leading-tight sm:text-base">
                <span className="shrink-0 text-muted-foreground">{icon}</span>
                <span className="truncate">{title}</span>
            </div>

            {!items?.length ? (
                <div className="text-sm text-muted-foreground">—</div>
            ) : (
                <div className="space-y-2">
                    {items.map((item) => (
                        <div key={item.name} className="min-w-0">
                            <div className="mb-1 flex min-w-0 items-center justify-between gap-2 text-xs leading-tight sm:text-sm">
                                <span className="min-w-0 truncate text-foreground/80">{item.name}</span>
                                <span className="shrink-0 price-tabular font-semibold">{item.count}</span>
                            </div>
                            <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                                <div
                                    className="h-full rounded-full bg-primary"
                                    style={{ width: `${Math.max(4, (item.count / max) * 100)}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

const PhonePanel = ({ info }) => (
    <div className="grid min-w-0 gap-3 sm:gap-4">
        <VerdictCard info={info} />

        <div className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
            <StatCard label="Cəmi" value={info.totalListings} />
            <StatCard label="Aktiv" value={info.closedListings}  />
            <StatCard label="Fərqli" value={info.distinctProperties} />
            <StatCard
                small
                label="Bazada"
                icon={<Clock size={14} weight="bold" className="shrink-0" />}
                value={info.firstSeen ? fmtDate(info.firstSeen) : "—"}
                sub={info.lastSeen ? `son: ${fmtDate(info.lastSeen)}` : undefined}
            />
        </div>

        <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            <BarList title="Saytlar" icon={<Globe size={16} weight="bold" />} items={info.sites} />
            <BarList title="Rayonlar" icon={<MapPin size={16} weight="bold" />} items={info.regions} />
            <BarList title="Əmlak növü" icon={<Buildings size={16} weight="bold" />} items={info.propertyTypes} />
            <BarList title="Əməliyyat" icon={<Tag size={16} weight="bold" />} items={info.operations} />
        </div>
    </div>
);

const Skeleton = () => (
    <div className="grid gap-3 sm:gap-4">
        <div className="h-20 rounded-xl bg-muted animate-pulse sm:h-24" />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
            {[0, 1, 2, 3].map((item) => (
                <div key={item} className="h-16 rounded-xl bg-muted animate-pulse sm:h-20" />
            ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
                <div key={item} className="h-24 rounded-xl bg-muted animate-pulse sm:h-32" />
            ))}
        </div>
    </div>
);

export const PhoneInfoDialog = ({ propertyId, open, onOpenChange }) => {
    const [idx, setIdx] = useState(0);

    const { data, isLoading, error } = useQuery({
        queryKey: ["phone-info", propertyId],
        queryFn: () => fetchPhoneInfo(propertyId),
        enabled: open && !!propertyId,
        retry: false,
    });

    useEffect(() => {
        if (!open) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onOpenChange(false);
        };

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        window.history.pushState({ phoneInfoOpen: true }, "");
        const handlePopState = () => {
            onOpenChange(false);
        };
        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("popstate", handlePopState);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onOpenChange]);

    useEffect(() => {
        if (open) setIdx(0);
    }, [open, propertyId]);

    if (!open || typeof document === "undefined") return null;

    const phones = data?.phones || [];
    const current = phones[idx] || phones[0];

    return createPortal(
        <div
            className="fixed inset-0 z-[2147483647] flex items-stretch justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onOpenChange(false);
            }}
            data-testid="phone-info-overlay"
        >
            <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="phone-info-title"
                data-testid="phone-info-dialog"
                className={`flex h-[100dvh] w-screen flex-col overflow-hidden rounded-none bg-card shadow-2xl sm:h-auto sm:max-h-[calc(100dvh-3rem)] sm:w-full sm:max-w-5xl sm:rounded-2xl lg:max-w-6xl ${borderClass}`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <header className="flex shrink-0 items-center justify-between gap-3 border-b border-black/10 bg-secondary/60 px-4 py-3 sm:px-6 sm:py-4">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary sm:h-11 sm:w-11">
                            <PhoneCall size={22} weight="bold" />
                        </div>
                        <div className="min-w-0">
                            <span id="phone-info-title" className="block truncate text-base font-semibold leading-tight sm:text-xl">
                                Nömrə axtarışı
                            </span>
                            <p className="mt-0.5 text-xs leading-tight text-muted-foreground sm:text-sm">
                                Sahibkar və ya vasitəçi · Elan statistikası
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        aria-label="Bağla"
                        data-testid="phone-info-close-btn"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-red-600 hover:text-white"
                    >
                        <X size={22} weight="bold" />
                    </button>
                </header>

                <div className="min-h-0 flex-1 overflow-y-auto p-3 pb-[max(2rem,env(safe-area-inset-bottom))] sm:p-6 sm:pb-6">
                    {isLoading && <Skeleton />}

                    {!isLoading && error && (
                        <div className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive sm:text-base">
                            <WarningCircle size={22} weight="fill" className="shrink-0" />
                            <span className="min-w-0 break-words">
                                {error?.message || "Məlumatı yükləmək mümkün olmadı."}
                            </span>
                        </div>
                    )}

                    {!isLoading && !error && phones.length === 0 && (
                        <div className="flex min-h-40 items-center justify-center rounded-xl border border-black/10 bg-secondary/40 p-6 text-center text-sm text-muted-foreground sm:text-base">
                            Bu elanda nömrə tapılmadı.
                        </div>
                    )}

                    {!isLoading && !error && phones.length > 0 && (
                        <div className="grid min-w-0 gap-3 sm:gap-4">
                            {phones.length > 1 && (
                                <div className="flex min-w-0 shrink-0 flex-wrap gap-2">
                                    {phones.map((phone, i) => (
                                        <button
                                            key={phone.phone}
                                            type="button"
                                            onClick={() => setIdx(i)}
                                            data-testid={`phone-info-tab-${i}`}
                                            className={`min-w-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold leading-tight price-tabular sm:text-sm ${i === idx
                                                    ? "border-primary bg-primary text-primary-foreground"
                                                    : "border-black/10 bg-card text-foreground/80 hover:border-primary"
                                                }`}
                                        >
                                            {phone.phoneFormatted}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {current && <PhonePanel info={current} />}
                        </div>
                    )}
                </div>
            </section>
        </div>,
        document.body
    );
};

export const PhoneInfoBtn = ({
    propertyId,
    variant = "icon",
    size = "w-6 h-6",
    iconSize = 12,
    beforeOpen,
    className = "",
}) => {
    const [open, setOpen] = useState(false);

    if (!getAccessToken()) return null;

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (beforeOpen && !beforeOpen()) return;
        setOpen(true);
    };

    return (
        <>
            {variant === "full" ? (
                <button
                    type="button"
                    onClick={handleClick}
                    data-testid="phone-info-btn"
                    className={`flex h-10 w-full items-center justify-center gap-1.5 rounded-full border border-primary/40 text-sm font-semibold text-primary transition-colors hover:bg-primary/5 ${className}`}
                >
                    <MagnifyingGlass size={14} weight="bold" />
                    Nömrə axtarışı
                </button>
            ) : (
                <button
                    type="button"
                    onClick={handleClick}
                    data-testid="phone-info-btn"
                    className={`${size} flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition hover:brightness-110 ${className}`}
                    aria-label="Nömrə axtarışı"
                    title="Nömrə axtarışı (Mülkiyyətçi / Vasitəçi)"
                >
                    <MagnifyingGlass size={iconSize} weight="bold" />
                </button>
            )}

            <PhoneInfoDialog
                propertyId={propertyId}
                open={open}
                onOpenChange={setOpen}
            />
        </>
    );
};